import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { maintenance } from "src/app/support/maintenance";
import { lastValueFrom } from "rxjs";
import { Box } from "src/app/modules/bootstrap/rocket-ui/backend/models/box";
import { ProductService } from "src/app/services/product.service";
import { SoftwareProduct } from "src/app/support/product";
import { sntLog } from "src/app/common/logger";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { BoxService } from "src/app/services/box.service";
import { BoxBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/box/box.backend";
import { ErrorCodes } from "src/app/modules/maintenance/error-codes";
import {
  LocalNetworkAccessQueryResult,
  LocalNetworkAccessService
} from "src/app/services/local-network-access.service";
import {
  TaskExecutionPlan,
  TasksService,
  TaskType
} from "src/app/services/tasks.service";
import { ToastService } from "src/app/services/toast.service";
import { attemptAsync } from "es-toolkit";

const logger = sntLog("ProvideBoxGuard");

@Injectable()
export class ProvideBoxGuard {
  readonly #bootstrapService = inject(BootstrapService);
  readonly #boxService = inject(BoxService);
  readonly #boxBackend = inject(BoxBackend);
  readonly #router = inject(Router);
  readonly #productService = inject(ProductService);
  readonly #localNetworkAccessService = inject(LocalNetworkAccessService);
  readonly #tasksService = inject(TasksService);
  readonly #notificationService = inject(ToastService);

  async canActivate(): Promise<boolean> {
    const currentProduct: SoftwareProduct =
      this.#productService.getCurrentProduct();

    if (
      currentProduct === SoftwareProduct.ADMIN ||
      currentProduct === SoftwareProduct.PLATFORM
    ) {
      return true;
    }

    // attemptAsync() perché la chiamata fallisce nel caso in cui non ci siano
    // membri con tenant di tipo "rocket"
    const [error, boxes] = await attemptAsync<Box[], Error>(() =>
      lastValueFrom(this.#boxBackend.listAll())
    );

    if (!!error || !boxes || boxes.length === 0) {
      switch (this.#productService.getCurrentProduct()) {
        case SoftwareProduct.ROCKET: {
          maintenance(this.#router, { code: ErrorCodes.ERR_002 });
          return false;
        }
        default: {
          return true;
        }
      }
    }

    const firstBox: Box = boxes[0];

    logger.debug(`current "${firstBox.address}"`);
    this.#bootstrapService.setCurrentBox(firstBox);

    const queryResult = await this.#localNetworkAccessService.query();

    logger.debug(`local-network-access state: ${queryResult}`);

    switch (queryResult) {
      case LocalNetworkAccessQueryResult.NOT_FOUND:
      case LocalNetworkAccessQueryResult.GRANTED: {
        this.#boxService.startContinuousCheck();
        break;
      }
      case LocalNetworkAccessQueryResult.DENIED: {
        this.#scheduleDeniedNotification();
        break;
      }

      case LocalNetworkAccessQueryResult.PROMPT: {
        this.#scheduleInfoNotification();
        this.#boxService.startContinuousCheck();
        break;
      }
    }

    return true;
  }

  #scheduleDeniedNotification(): void {
    this.#tasksService.schedule({
      plan: TaskExecutionPlan.WHEN_USER_READY,
      type: TaskType.RUN_ONCE,
      fn: () => {
        this.#notificationService.error({
          sticky: true,
          title: sntTranslate(
            marker(
              "L\'accesso alla rete locale è disabilitato. Consulta l\'help center per maggiori informazioni su come abilitarlo."
            )
          ),
          link: "https://helpcenter.snoots.pet/",
          linkText: sntTranslate(
            marker("Clicca qui per aprire il centro di assistenza")
          ),
          image: "/assets/images/permission_local_network_access_info_allow.gif"
        });
      }
    });
  }

  #scheduleInfoNotification(): void {
    this.#tasksService.schedule({
      plan: TaskExecutionPlan.WHEN_USER_READY,
      type: TaskType.RUN_ONCE,
      fn: () => {
        this.#notificationService.info({
          sticky: true,
          title: sntTranslate(
            marker(
              "L\'accesso alla rete locale dev\'essere autorizzato, senza di esso il box non può funzionare correttamente. Premi su consenti nel popup in alto."
            )
          ),
          image: "/assets/images/permission_local_network_access_example.png",
          link: "https://helpcenter.snoots.pet/",
          linkText: sntTranslate(
            marker(
              "Clicca qui nel caso non apparisse il popup di autorizzazione"
            )
          )
        });
      }
    });
  }
}
